import { useEffect, useRef, useState } from 'react';

const tabs = [
  {
    id: 'bay',
    icon: 'ri-car-line',
    label: 'Service Bays',
    color: 'text-[#f97316]',
    activeBg: 'bg-[#f97316]/10 border-[#f97316]/40',
    heading: 'Full Service Bay Overview — At a Glance',
    description:
      'See every bay\'s live status on a single screen. Know which bays are occupied, which techs are assigned, and how long each job has been running. No more walking the floor to find out.',
    bullets: [
      { icon: 'ri-layout-grid-line', text: 'Visual bay map with drag-to-assign' },
      { icon: 'ri-time-line', text: 'Job duration timers per bay' },
      { icon: 'ri-alarm-line', text: 'Alerts for overdue or stalled jobs' },
      { icon: 'ri-swap-box-line', text: 'One-click technician reassignment' },
    ],
    image: 'https://public.readdy.ai/ai/img_res/59530033af4fb0e7ff5bd5ea5b357030.jpg',
    mockupContent: (
      <div className="bg-[#060f1e] rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-xs font-semibold">Bay Overview — Today</span>
          <span className="text-xs text-gray-400">8 Bays Total</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { bay: 'Bay 1', tech: 'Sipho M.', status: 'In Progress', color: 'border-[#f97316]/50 bg-[#f97316]/5' },
            { bay: 'Bay 2', tech: 'Ruan V.', status: 'Diagnosing', color: 'border-yellow-400/50 bg-yellow-400/5' },
            { bay: 'Bay 3', tech: 'Thabo N.', status: 'Ready', color: 'border-green-400/50 bg-green-400/5' },
            { bay: 'Bay 4', tech: '—', status: 'Empty', color: 'border-white/10 bg-white/5' },
            { bay: 'Bay 5', tech: 'Keegan L.', status: 'In Progress', color: 'border-[#f97316]/50 bg-[#f97316]/5' },
            { bay: 'Bay 6', tech: 'Sipho M.', status: 'Waiting', color: 'border-yellow-400/50 bg-yellow-400/5' },
            { bay: 'Bay 7', tech: '—', status: 'Empty', color: 'border-white/10 bg-white/5' },
            { bay: 'Bay 8', tech: 'Ruan V.', status: 'Complete', color: 'border-green-400/50 bg-green-400/5' },
          ].map((b) => (
            <div key={b.bay} className={`rounded-lg p-2 border ${b.color} text-center`}>
              <div className="text-white text-xs font-bold">{b.bay}</div>
              <div className="text-gray-400 text-xs mt-0.5 truncate">{b.tech}</div>
              <div className={`text-xs mt-1 font-medium truncate ${
                b.status === 'Ready' || b.status === 'Complete' ? 'text-green-400'
                : b.status === 'Empty' ? 'text-gray-500'
                : b.status === 'In Progress' ? 'text-[#f97316]'
                : 'text-yellow-400'
              }`}>{b.status}</div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'jobcard',
    icon: 'ri-file-list-3-line',
    label: 'Job Cards',
    color: 'text-[#3b82f6]',
    activeBg: 'bg-[#3b82f6]/10 border-[#3b82f6]/40',
    heading: 'Digital Job Cards — Replacing the Paper Trail',
    description:
      'Create, assign, and track every service job digitally. Each job card captures vehicle info, service details, parts used, labour hours, and customer notes — all in one place.',
    bullets: [
      { icon: 'ri-qr-code-line', text: 'Scan VIN or plate to auto-fill history' },
      { icon: 'ri-list-check-2', text: 'Checklist-based inspection forms' },
      { icon: 'ri-image-add-line', text: 'Photo capture for damage documentation' },
      { icon: 'ri-edit-line', text: 'Customer e-sign on job approval' },
    ],
    image: 'https://readdy.ai/api/search-image?query=digital%20job%20card%20creation%20interface%20on%20tablet%20showing%20automotive%20service%20checklist%20form%20dark%20navy%20background%20electric%20blue%20accent%20lights%20modern%20workshop%20management%20software%20realistic%20UI%20screenshot%20South%20Africa&width=800&height=500&seq=ws002&orientation=landscape',
    mockupContent: (
      <div className="bg-[#060f1e] rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-xs font-semibold">Job Card #JC-2847</span>
          <span className="text-xs text-[#3b82f6] bg-[#3b82f6]/10 px-2 py-0.5 rounded-full">Open</span>
        </div>
        <div className="space-y-2 mb-3">
          {[
            { label: 'Vehicle', value: 'Toyota Hilux — GP 45 ABC' },
            { label: 'Mileage', value: '87,340 km' },
            { label: 'Service', value: 'Full Service + Brakes' },
            { label: 'Technician', value: 'Sipho M. — Bay 3' },
          ].map((r) => (
            <div key={r.label} className="flex gap-2">
              <span className="text-gray-500 text-xs w-20 flex-shrink-0">{r.label}</span>
              <span className="text-gray-200 text-xs">{r.value}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 pt-2">
          <span className="text-gray-400 text-xs font-semibold block mb-1.5">Checklist</span>
          {[
            { task: 'Engine oil replaced', done: true },
            { task: 'Brake pads front — replaced', done: true },
            { task: 'Tyre pressure check', done: false },
            { task: 'Customer sign-off', done: false },
          ].map((c) => (
            <div key={c.task} className="flex items-center gap-2 mb-1">
              <div className={`w-3.5 h-3.5 flex items-center justify-center rounded flex-shrink-0 ${c.done ? 'bg-[#3b82f6]' : 'border border-white/20'}`}>
                {c.done && <i className="ri-check-line text-white text-xs" style={{ fontSize: '8px' }}></i>}
              </div>
              <span className={`text-xs ${c.done ? 'text-gray-400 line-through' : 'text-gray-200'}`}>{c.task}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'mobile',
    icon: 'ri-smartphone-line',
    label: 'Technician App',
    color: 'text-green-400',
    activeBg: 'bg-green-400/10 border-green-400/40',
    heading: 'Mobile App — Your Techs Stay Connected',
    description:
      'Your technicians don\'t need to leave the bay to update job status. The PRODIFY mobile app lets them update progress, request parts, capture photos, and flag issues on the spot.',
    bullets: [
      { icon: 'ri-refresh-line', text: 'Update job status from anywhere in the shop' },
      { icon: 'ri-shopping-cart-line', text: 'Request parts directly from the bay' },
      { icon: 'ri-camera-line', text: 'Photo evidence attached to job card' },
      { icon: 'ri-message-2-line', text: 'Chat with service advisor in-app' },
    ],
    image: 'https://readdy.ai/api/search-image?query=automotive%20technician%20using%20mobile%20app%20in%20workshop%20garage%20dark%20environment%20phone%20screen%20showing%20job%20update%20interface%20green%20accent%20lights%20hand%20holding%20smartphone%20modern%20service%20centre%20professional%20photography%20realistic&width=800&height=500&seq=ws003&orientation=landscape',
    mockupContent: (
      <div className="bg-[#060f1e] rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-xs font-semibold">Technician View</span>
          <span className="flex items-center gap-1 text-green-400 text-xs">
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
            Online
          </span>
        </div>
        <div className="flex gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center bg-green-400/10 rounded-full flex-shrink-0">
            <i className="ri-user-line text-green-400 text-sm"></i>
          </div>
          <div>
            <div className="text-white text-xs font-semibold">Sipho Mhlongo</div>
            <div className="text-gray-400 text-xs">Bay 3 · 2 Active Jobs</div>
          </div>
        </div>
        <div className="space-y-2">
          {[
            { job: 'JC-2847 — Hilux Full Service', action: 'Update Status', color: 'text-[#f97316] bg-[#f97316]/10' },
            { job: 'JC-2851 — Polo Oil Change', action: 'Request Part', color: 'text-green-400 bg-green-400/10' },
          ].map((j) => (
            <div key={j.job} className="bg-[#0a1628] rounded-lg px-3 py-2 flex items-center justify-between">
              <span className="text-gray-200 text-xs">{j.job}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${j.color}`}>{j.action}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 p-2 bg-[#3b82f6]/10 border border-[#3b82f6]/20 rounded-lg flex items-start gap-2">
          <i className="ri-message-2-line text-[#3b82f6] text-sm flex-shrink-0 mt-0.5"></i>
          <span className="text-gray-300 text-xs">Service Advisor: "Customer confirmed brake pad upgrade. Proceed."</span>
        </div>
      </div>
    ),
  },
];

const WorkshopFeatures = () => {
  const [activeTab, setActiveTab] = useState('bay');
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = tabs.find((t) => t.id === activeTab) ?? tabs[0];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#060f1e] py-24 px-6" id="workshop" ref={ref}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-tools-line text-[#f97316] text-sm"></i>
            <span className="text-gray-300 text-xs font-semibold tracking-widest uppercase">Workshop Deep Dive</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Built for the Workshop Floor,
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#f97316] to-[#3b82f6]">
              Not Just the Office
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            From the service bay to the mobile app — every part of your workshop operation is covered.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === tab.id
                  ? `${tab.activeBg} ${tab.color}`
                  : 'border-white/10 text-gray-400 hover:border-white/20 hover:text-gray-200'
              }`}
            >
              <i className={`${tab.icon} text-base`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content area */}
        <div
          key={activeTab}
          className={`grid lg:grid-cols-2 gap-10 items-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {/* Left — image + mockup */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-white/10 w-full h-64 md:h-80">
              <img
                src={active.image}
                alt={active.heading}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060f1e]/80 to-transparent"></div>
            </div>
            {/* Overlay mockup card */}
            <div className="mt-4">
              {active.mockupContent}
            </div>
          </div>

          {/* Right — text */}
          <div>
            <div className={`w-12 h-12 flex items-center justify-center ${active.activeBg} rounded-xl mb-5 border`}>
              <i className={`${active.icon} ${active.color} text-2xl`}></i>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {active.heading}
            </h3>
            <p className="text-gray-400 text-base leading-relaxed mb-8">
              {active.description}
            </p>
            <ul className="space-y-4">
              {active.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className={`w-9 h-9 flex items-center justify-center ${active.activeBg} rounded-lg flex-shrink-0 border`}>
                    <i className={`${b.icon} ${active.color} text-base`}></i>
                  </div>
                  <span className="text-gray-300 text-sm leading-relaxed pt-1.5">{b.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="#demo"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#f97316] hover:bg-[#ea6c0a] text-white font-semibold rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
              >
                <i className="ri-calendar-check-line"></i>
                Book a Live Demo
              </a>
              <a
                href="#features"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
              >
                <i className="ri-arrow-right-line"></i>
                Explore All Features
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkshopFeatures;
